// Eager hero shell — kept deliberately small so the LCP <img> can mount
// in the first React commit. No barrel imports (would pull Confetti,
// Polaroid, Lottie, etc. into the eager chunk), no JsonLd (head writes
// can wait), no useEffect timers. See PERF-LCP-1 in changelog.md.
import { type RefObject } from "react";
import { Link } from "react-router-dom";
// Direct file imports — barrel import (`@/components/confetti`) would
// drag the entire confetti surface into the eager chunk.
import { CButton } from "@/components/confetti/CButton";
import { Seo } from "@/components/Seo";
import {
  CAMPAIGN_CODE,
  CAMPAIGN_COPY,
  CAMPAIGN_DISCOUNTED_PRICE_LABEL,
  CAMPAIGN_FULL_PRICE_LABEL,
  isCampaignActive,
} from "@/lib/campaign";

const SITE_URL = "https://confetticam.com";

// Hero is served from /public so the <link rel="preload"> in index.html
// dedupes against this <img>. URLs MUST match exactly.
const landingHero = "/landing-hero.webp";
const landingHero720 = "/landing-hero-720.webp";

const HERO_IMAGE_ALT =
  "Confetti in action: a guest's phone showing a personal wedding photo mission, surrounded by polaroids of guest moments, a scan-to-join QR card, and a no-app-needed badge.";

const HeroImage = ({
  className = "",
  decorative = false,
}: {
  className?: string;
  decorative?: boolean;
}) => (
  <div
    className={`mx-auto w-full max-w-[680px] overflow-hidden rounded-[24px] shadow-[0_18px_50px_rgba(24,24,27,0.10)] lg:max-w-none ${className}`}
  >
    <img
      src={landingHero}
      srcSet={`${landingHero720} 720w, ${landingHero} 1448w`}
      sizes="(min-width: 1024px) 560px, 100vw"
      alt={decorative ? "" : HERO_IMAGE_ALT}
      aria-hidden={decorative ? true : undefined}
      width={1448}
      height={1086}
      loading="eager"
      decoding="async"
      // @ts-expect-error - fetchpriority is a valid HTML attribute
      fetchpriority="high"
      className="aspect-[4/3] w-full object-cover object-center"
    />
  </div>
);

interface HeroProps {
  heroCtaRef: RefObject<HTMLDivElement>;
}

export default function Hero({ heroCtaRef }: HeroProps) {
  const campaignActive = isCampaignActive();
  const ctaLabel = campaignActive ? CAMPAIGN_COPY.heroCtaLabel : "Create your wedding game";
  const priceChip = campaignActive ? CAMPAIGN_COPY.heroChipPrice : "£79 one-off";
  return (
    <>
      <Seo
        title="Wedding Photo Game for Guests — Personal Challenges & QR Code | Confetti"
        description="Set a personal photo challenge for every UK wedding guest. One QR code, no app, candid photos your photographer can't take. £79 one-off."
        canonical={SITE_URL + "/"}
        ogImage={`${SITE_URL}/og-image.webp`}
        ogImageAlt="Confetti — wedding guests playing a personal photo challenge on their phones."
      />

      {/* HERO */}
      <section className="relative overflow-hidden px-4 pt-12 pb-10 text-center sm:px-8 sm:pt-20 sm:pb-14">
        <div className="relative z-[2] mx-auto max-w-[1120px] lg:grid lg:grid-cols-2 lg:items-center lg:gap-12 lg:text-left">
          {/* Text column */}
          <div className="mx-auto max-w-[640px] lg:mx-0">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-soft px-3.5 py-1.5 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-deep sm:text-[12px]">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-deep" />A wedding photo game<span className="h-1.5 w-1.5 rounded-full bg-accent-deep" />
            </div>

            {campaignActive && (
              <p className="mb-5 font-display text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-deep sm:text-[12px]">
                {CAMPAIGN_COPY.heroEyebrow}
              </p>
            )}

            <h1
              className="font-display text-ink"
              style={{
                fontSize: "clamp(36px, 8vw, 64px)",
                lineHeight: 1.06,
                letterSpacing: "-0.02em",
                fontWeight: 500,
                textWrap: "balance" as never,
                margin: "0 0 16px",
                overflowWrap: "break-word",
                hyphens: "auto",
              }}
            >
              A wedding game your guests actually play.
            </h1>

            <p
              className="mx-auto font-body text-text-muted lg:mx-0"
              style={{
                fontSize: "clamp(16px, 2.4vw, 20px)",
                lineHeight: 1.5,
                maxWidth: 580,
                margin: "0 auto 14px",
              }}
            >
              The photos only your guests could take.
            </p>

            <p
              className="mx-auto mb-7 font-body italic text-text-subtle lg:mx-0"
              style={{
                fontSize: "clamp(13px, 1.8vw, 15px)",
                lineHeight: 1.5,
                maxWidth: 520,
              }}
            >
              For couples who want guest photos as unique as their guests, without disposable cameras, hashtags, or chasing
              people on WhatsApp.
            </p>

            <div ref={heroCtaRef} className="flex flex-col items-center gap-3 lg:items-start">
              {campaignActive && (
                <p className="font-body text-[14px] leading-snug text-ink/80 sm:text-[15px]">
                  <span className="font-semibold text-accent-deep">
                    Now {CAMPAIGN_DISCOUNTED_PRICE_LABEL}
                  </span>
                  <span className="mx-2 text-ink/30" aria-hidden="true">•</span>
                  <span className="text-text-muted line-through">
                    Usually {CAMPAIGN_FULL_PRICE_LABEL}
                  </span>
                  <span className="mx-2 text-ink/30" aria-hidden="true">•</span>
                  <span>Use code </span>
                  <span className="font-semibold text-accent-deep">{CAMPAIGN_CODE}</span>
                </p>
              )}
              <CButton
                as={Link}
                to="/auth?mode=signup"
                variant="primary"
                size="lg"
                iconRight={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M13 6l6 6-6 6"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                }
              >
                {ctaLabel}
              </CButton>

              <p className="mt-1 text-center text-xs text-slate-500 lg:text-left">
                <span className="text-emerald-500" aria-hidden="true">✓</span>{" "}
                14-day refund before photos are uploaded
              </p>

              {/* Mobile pills — 3 items, no price, single line guaranteed */}
              <div className="mt-1 flex flex-nowrap items-center justify-center gap-1.5 sm:hidden">
                {["14-day refund", "No app needed", "10 min setup"].map((c) => (
                  <span
                    key={c}
                    className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-accent-deep/15 bg-accent-soft/60 px-2.5 py-1.5 font-body text-[13px] font-medium leading-none text-ink/80"
                  >
                    {c}
                  </span>
                ))}
              </div>

              {/* Desktop/tablet pills — 4 items including price */}
              <div className="mt-1 hidden flex-nowrap items-center justify-center gap-2 sm:flex lg:justify-start">
                {[priceChip, "No app needed", "10 min setup", "14-day refund"].map((c) => (
                  <span
                    key={c}
                    className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-accent-deep/15 bg-accent-soft/60 px-3.5 py-2 font-body text-[13px] font-medium leading-none text-ink/80"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            {/* Hero image — mobile only (between chips and tagline) */}
            <HeroImage className="mt-7 lg:hidden" />

            <p
              className="mt-7 font-script italic text-text-muted"
              style={{ fontSize: "clamp(20px, 2.6vw, 24px)", lineHeight: 1.35, letterSpacing: "0.01em" }}
            >
              “The photos your photographer
              <span
                className="font-script italic text-accent-deep px-[8px]"
                style={{ fontWeight: 600, fontSize: "1.4em", lineHeight: 1 }}
              >
                can&rsquo;t
              </span>
              take.”
            </p>
          </div>

          {/* Hero image — desktop right column */}
          <div className="hidden lg:block">
            <HeroImage decorative />
          </div>
        </div>
      </section>
    </>
  );
}
